/**
 * Revenue Tab Component
 * Features: CSV/PDF export, AR aging visualization, contextual empty state
 */

import { useState } from "react";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { 
  ChartCard, TableCard, CustomTooltip, DataTable, 
  TableRow, TableCell, EmptyState, EMPTY_STATES 
} from "./shared";
import {
  CHART_COLORS, CHART_STYLE, formatCurrency, formatCompactCurrency,
  formatMonthLabel, exportToCSV, DEFAULT_ROW_COUNT,
  generateAnalyticsPdfContent, generateTableHtml,
} from "../../lib/chartUtils";
import useExportDialog from "../../hooks/useExportDialog";

const RevenueTab = ({ data }) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const { exportToPdf } = useExportDialog();

  if (!data) {
    return (
      <EmptyState 
        icon={TrendingUp}
        {...EMPTY_STATES.revenue}
      />
    );
  }

  const { monthly_trend = [], ar_aging = {} } = data;
  
  if (monthly_trend.length === 0 && Object.keys(ar_aging).length === 0) {
    return (
      <EmptyState 
        icon={TrendingUp}
        {...EMPTY_STATES.revenue}
      />
    );
  }

  const totalOutstanding = (ar_aging.current || 0) + (ar_aging["30_days"] || 0) + 
                          (ar_aging["60_days"] || 0) + (ar_aging["90_plus"] || 0);

  const handleExportCSV = () => {
    exportToCSV(monthly_trend, "revenue-cashflow",
      ["Month", "Billings Sent", "Billings Received", "Contract Value"],
      {
        "Month": "month",
        "Billings Sent": "billings_sent",
        "Billings Received": "billings_received",
        "Contract Value": "contract_value",
      }
    );
  };

  const handleExportPDF = () => {
    const tableRows = monthly_trend.slice(0, rowCount).map(row => [
      formatMonthLabel(row.month),
      formatCurrency(row.billings_sent),
      formatCurrency(row.billings_received),
      formatCurrency(row.contract_value),
    ]);

    const tableHtml = generateTableHtml(
      ["Month", "Billings Sent", "Billings Received", "Contract Value"],
      tableRows,
      ["left", "right", "right", "right"]
    );

    const arHtml = `
      <table style="width: 50%; margin-bottom: 16px;">
        <tr><td>Current (0-30 days)</td><td class="text-right green">${formatCurrency(ar_aging.current || 0)}</td></tr>
        <tr><td>31-60 days</td><td class="text-right yellow">${formatCurrency(ar_aging["30_days"] || 0)}</td></tr>
        <tr><td>61-90 days</td><td class="text-right" style="color: #f97316;">${formatCurrency(ar_aging["60_days"] || 0)}</td></tr>
        <tr><td>90+ days</td><td class="text-right red">${formatCurrency(ar_aging["90_plus"] || 0)}</td></tr>
        <tr style="font-weight: bold; border-top: 2px solid #333;"><td>Total Outstanding</td><td class="text-right">${formatCurrency(totalOutstanding)}</td></tr>
      </table>
    `;

    const content = generateAnalyticsPdfContent("Revenue & Cash Flow Report", [
      { title: "Accounts Receivable Aging", content: arHtml },
      { title: "Monthly Billings Trend", content: tableHtml },
    ]);

    exportToPdf("Revenue & Cash Flow Report", content);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AR Aging Cards */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Accounts Receivable Aging</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
              <span className="text-sm">Current (0-30 days)</span>
              <span className="font-bold text-green-600">{formatCurrency(ar_aging.current || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
              <span className="text-sm">31-60 days</span>
              <span className="font-bold text-yellow-600">{formatCurrency(ar_aging["30_days"] || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-orange-50 dark:bg-orange-950 rounded-lg">
              <span className="text-sm">61-90 days</span>
              <span className="font-bold text-orange-600">{formatCurrency(ar_aging["60_days"] || 0)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
              <span className="text-sm">90+ days</span>
              <span className="font-bold text-red-600">{formatCurrency(ar_aging["90_plus"] || 0)}</span>
            </div>
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Outstanding</span>
                <span className="font-bold text-lg">{formatCurrency(totalOutstanding)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Trend Chart */}
        <ChartCard title="Monthly Billings Trend" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={288}>
            <LineChart data={monthly_trend} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={CHART_STYLE.axis} />
              <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
              <Tooltip content={<CustomTooltip formatter={formatCurrency} />} labelFormatter={formatMonthLabel} />
              <Legend />
              <Line type="monotone" dataKey="billings_sent" name="Sent" stroke={CHART_COLORS.info} strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="billings_received" name="Received" stroke={CHART_COLORS.success} strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Contract Value Trend */}
      <ChartCard title="Contract Value by Month" className="">
        <ResponsiveContainer width="100%" height={256}>
          <BarChart data={monthly_trend} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
            <CartesianGrid {...CHART_STYLE.grid} />
            <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={CHART_STYLE.axis} />
            <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
            <Tooltip content={<CustomTooltip formatter={formatCurrency} />} labelFormatter={formatMonthLabel} />
            <Bar dataKey="contract_value" name="Contract Value" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Monthly Revenue Table */}
      <TableCard 
        title="Monthly Revenue Details"
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        rowCount={rowCount}
        onRowCountChange={setRowCount}
      >
        <DataTable headers={[
          { label: "Month" },
          { label: "Billings Sent", align: "right" },
          { label: "Billings Received", align: "right" },
          { label: "Contract Value", align: "right" },
        ]}>
          {monthly_trend.slice(0, rowCount).map((row) => (
            <TableRow key={row.month} className="table-row">
              <TableCell className="font-medium">{formatMonthLabel(row.month)}</TableCell>
              <TableCell className="text-right text-yellow-600">{formatCurrency(row.billings_sent)}</TableCell>
              <TableCell className="text-right text-green-600">{formatCurrency(row.billings_received)}</TableCell>
              <TableCell className="text-right">{formatCurrency(row.contract_value)}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      </TableCard>
    </div>
  );
};

export default RevenueTab;
